/**
 * "Replay Failure" feature — a self-contained UI module.
 *
 * Records the members that break during a collapse (in the order they failed)
 * and lets the user step through them frame-by-frame with Prev/Next while the
 * camera zooms in on each failed part.
 *
 * This module owns ONLY its own DOM (built inside the `container` passed to the
 * constructor) and talks to the rest of the app exclusively through `ReplayHooks`.
 * The maintainer wires the hooks and calls `record()` / `reset()` from the frame
 * loop (see the integration notes at the bottom of this file).
 */

/** Bridge to the host app's camera and simulation. Implemented by the caller. */
export interface ReplayHooks {
  /** Smoothly move the camera to look at a world point, framing `radius` metres. */
  focusOn(target: { x: number; y: number; z: number }, radius: number): void;
  /** Freeze/unfreeze the live sim during replay. */
  setPaused(paused: boolean): void;
  /** Snapshot current node positions (x,y,z per node) so a moment can be restored. */
  captureSnapshot(): Float32Array;
  /** Restore node positions from a snapshot and re-render — rewinds to a failure moment. */
  applySnapshot(positions: Float32Array): void;
}

import { getPhenomenon } from "@/physics/phenomena";

/** One recorded failure, in chronological order. */
export interface FailureEvent {
  position: { x: number; y: number; z: number };
  label: string;
  util: number;
  time: number;
  phenomenonId?: string; // root-cause phenomenon (buckling, fracture, …)
  detail?: string; // the actual computed physics (force vs Euler Pcr / A·Fy, etc.)
  snapshot: Float32Array; // node positions at the instant this part failed (rewind target)
}

/** What the caller passes to `record()` for each member that failed this frame. */
export interface FailedMemberInput {
  /** Stable unique id for this failure (e.g. member node-pair). Used for de-dup so
   *  a cascade of same-section members isn't collapsed into one entry by label. */
  key: string;
  position: { x: number; y: number; z: number };
  label: string;
  util: number;
  phenomenonId?: string;
  detail?: string; // real numbers from the model for this specific failure
}

/** Camera framing radius (m) when zoomed in on a single failed member. */
const FOCUS_RADIUS = 4;
/** Framing radius (m) used on exit to pull the camera back out to the structure. */
const WIDE_RADIUS = 40;

export class FailureReplay {
  private readonly hooks: ReplayHooks;
  private readonly events: FailureEvent[] = [];
  /** De-dup guard: stable keys already recorded (a member fails at most once). */
  private readonly seen = new Set<string>();

  private replaying = false;
  private index = 0;
  /** Live (collapsed) geometry saved when replay starts, restored on exit. */
  private liveSnapshot: Float32Array | null = null;

  // --- owned DOM ---
  private readonly root: HTMLDivElement;
  private readonly playBtn: HTMLButtonElement;
  private readonly panel: HTMLDivElement;
  private readonly prevBtn: HTMLButtonElement;
  private readonly nextBtn: HTMLButtonElement;
  private readonly exitBtn: HTMLButtonElement;
  private readonly counter: HTMLSpanElement;
  private readonly caption: HTMLDivElement;
  private readonly detailEl: HTMLDivElement;
  private readonly explain: HTMLDivElement;
  private readonly learn: HTMLAnchorElement;

  constructor(container: HTMLElement, hooks: ReplayHooks) {
    this.hooks = hooks;

    const root = document.createElement("div");
    root.style.cssText = [
      "position:relative",
      "display:flex",
      "flex-direction:column",
      "gap:8px",
      "font:15px/1.45 system-ui,sans-serif",
      "color:#dfe6ef",
    ].join(";");

    // --- "Replay Failure" trigger button (hidden until a failure exists) ---
    const playBtn = document.createElement("button");
    playBtn.textContent = "▶ Replay Failure";
    playBtn.style.cssText = this.buttonCss("#151b24");
    playBtn.style.display = "none";
    playBtn.addEventListener("click", () => this.start());

    // --- replay controls panel (hidden unless replaying) ---
    const panel = document.createElement("div");
    panel.style.cssText = [
      "display:none",
      "flex-direction:column",
      "gap:9px",
      "padding:16px",
      "min-width:340px",
      "max-width:420px",
      "border-radius:10px",
      "background:#0b0e13",
      "border:1px solid #232c38",
    ].join(";");

    const caption = document.createElement("div");
    caption.style.cssText = "min-height:18px;font-weight:700;font-size:16px;color:#ffd9d0";

    // the actual computed physics for THIS failure (real numbers from the model)
    const detailEl = document.createElement("div");
    detailEl.style.cssText = "font-size:14px;line-height:1.55;color:#e7d9a6;font-family:ui-monospace,monospace;white-space:pre-line";

    // root-cause phenomenon explanation + a "learn more" citation link
    const explain = document.createElement("div");
    explain.style.cssText = "font-size:13.5px;line-height:1.55;color:#b9c6d6;font-weight:400";
    const learn = document.createElement("a");
    learn.target = "_blank";
    learn.rel = "noopener noreferrer";
    learn.style.cssText = "font-size:12px;color:#4fd1c5;text-decoration:none;font-weight:600";

    const controls = document.createElement("div");
    controls.style.cssText = "display:flex;align-items:center;gap:8px";

    const prevBtn = document.createElement("button");
    prevBtn.textContent = "◀ Prev";
    prevBtn.style.cssText = this.buttonCss("#151b24");
    prevBtn.addEventListener("click", () => this.step(-1));

    const counter = document.createElement("span");
    counter.style.cssText = "flex:1;text-align:center;color:#9fb0c4;font-variant-numeric:tabular-nums";

    const nextBtn = document.createElement("button");
    nextBtn.textContent = "Next ▶";
    nextBtn.style.cssText = this.buttonCss("#151b24");
    nextBtn.addEventListener("click", () => this.step(1));

    const exitBtn = document.createElement("button");
    exitBtn.textContent = "✕ Exit";
    exitBtn.style.cssText = this.buttonCss("#2a1416");
    exitBtn.addEventListener("click", () => this.exit());

    controls.append(prevBtn, counter, nextBtn, exitBtn);
    panel.append(caption, detailEl, explain, learn, controls);
    root.append(playBtn, panel);
    container.appendChild(root);

    this.root = root;
    this.playBtn = playBtn;
    this.panel = panel;
    this.prevBtn = prevBtn;
    this.nextBtn = nextBtn;
    this.exitBtn = exitBtn;
    this.counter = counter;
    this.caption = caption;
    this.detailEl = detailEl;
    this.explain = explain;
    this.learn = learn;
  }

  private buttonCss(bg: string): string {
    return [
      "appearance:none",
      "cursor:pointer",
      "padding:6px 12px",
      "border-radius:6px",
      "border:1px solid #2b3644",
      `background:${bg}`,
      "color:#dfe6ef",
      "font:inherit",
      "font-weight:600",
    ].join(";");
  }

  /**
   * Called every sim frame with the members that failed THIS frame. Appends a
   * de-duplicated event per member, preserving chronological order. Empty frames
   * are ignored.
   */
  record(failed: FailedMemberInput[], time: number, snapshot: Float32Array): void {
    if (failed.length === 0) return;
    for (const f of failed) {
      if (this.seen.has(f.key)) continue; // de-dup by stable id, not display label
      this.seen.add(f.key);
      this.events.push({
        position: { x: f.position.x, y: f.position.y, z: f.position.z },
        label: f.label,
        util: f.util,
        time,
        phenomenonId: f.phenomenonId,
        detail: f.detail,
        snapshot,
      });
    }
    // A newly-recorded failure makes the Replay button available (unless the
    // panel is currently open).
    if (!this.replaying) this.playBtn.style.display = this.events.length > 0 ? "" : "none";
  }

  /** Clear recorded events. Called on tower rebuild / reset. */
  reset(): void {
    if (this.replaying) this.exit();
    this.events.length = 0;
    this.seen.clear();
    this.index = 0;
    this.playBtn.style.display = "none";
  }

  dispose(): void {
    if (this.replaying) this.hooks.setPaused(false);
    this.root.remove();
  }

  // --- replay flow ---

  private start(): void {
    if (this.events.length === 0) return;
    this.replaying = true;
    this.index = 0;
    // remember the live (collapsed) state so we can restore it on exit
    this.liveSnapshot = this.hooks.captureSnapshot();
    this.hooks.setPaused(true);
    this.playBtn.style.display = "none";
    this.panel.style.display = "flex";
    this.focusCurrent();
  }

  private step(dir: 1 | -1): void {
    const next = this.index + dir;
    if (next < 0 || next >= this.events.length) return;
    this.index = next;
    this.focusCurrent();
  }

  private exit(): void {
    this.replaying = false;
    this.panel.style.display = "none";
    this.playBtn.style.display = this.events.length > 0 ? "" : "none";
    // restore the live (collapsed) geometry before resuming the sim
    if (this.liveSnapshot) this.hooks.applySnapshot(this.liveSnapshot);
    this.hooks.setPaused(false);
    // Pull the camera back out to a wide view of the whole scene.
    this.hooks.focusOn({ x: 0, y: 0, z: 0 }, WIDE_RADIUS);
  }

  private focusCurrent(): void {
    const ev = this.events[this.index];
    if (!ev) return;
    // rewind the tower to how it looked when this part failed, so the broken
    // member is shown IN SITU (not where its debris later landed)
    this.hooks.applySnapshot(ev.snapshot);
    this.hooks.focusOn(ev.position, FOCUS_RADIUS);

    const order = this.index + 1;
    this.counter.textContent = `[${order}/${this.events.length}]`;
    this.caption.textContent = `${ev.label} — ${Math.round(ev.util * 100)}% · t=${ev.time.toFixed(1)}s`;
    this.detailEl.textContent = ev.detail ?? "";

    // root-cause explanation + citation for this failure
    const ph = ev.phenomenonId ? getPhenomenon(ev.phenomenonId) : undefined;
    if (ph) {
      this.explain.textContent = ph.explanation;
      this.learn.href = ph.moreInfoUrl;
      this.learn.textContent = `Learn more: ${ph.name} ↗`;
      this.learn.style.display = "";
    } else {
      this.explain.textContent = "";
      this.learn.style.display = "none";
    }

    this.prevBtn.disabled = this.index === 0;
    this.nextBtn.disabled = this.index === this.events.length - 1;
    const dim = "0.45";
    this.prevBtn.style.opacity = this.prevBtn.disabled ? dim : "1";
    this.nextBtn.style.opacity = this.nextBtn.disabled ? dim : "1";
    void this.exitBtn; // retained for symmetry; always enabled
  }
}
